"use client";

// The netti beside a reader page IS the scroll bar.
//
// Every long-form reader (a company post, a shared page, a skill) seats the
// organism on the right and drives its AGE from how far you've read: it starts
// as a young bloom and is fully grown by the last line. The signal rides the
// imperative age bus (`innernet:organism-age`, see MemoryOrganism) so scrubbing
// is a uniform update at 60fps — never a React re-render.
//
// The reading veil is untouched: the ramp that frosts the organism away (and
// rests its renderer) still runs; this only decides how grown the creature is
// when you glimpse it through the frost.

import { useEffect } from "react";
import { onStageScroll, stageScrollMax, stageScrollTop } from "../stage/scroller";

/** where a letter's netti starts — young, but recognisably itself */
const START_AGE = 0.38;

/** dispatch one age value onto the bus (null releases back to natural size). */
function publishAge(age: number | null) {
  window.dispatchEvent(new CustomEvent("innernet:organism-age", { detail: { age } }));
}

/**
 * Drive the organism's age from reading progress for the life of the
 * component: the stage's scroller when the page stands on a stage, the
 * document otherwise (lib/stage/scroller). Pass `enabled: false` to sit out
 * (e.g. a share page still loading, or its preview in the studio).
 */
export function useReadAge(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    let last = -1;
    const write = () => {
      const max = stageScrollMax();
      const p = max > 0 ? Math.max(0, Math.min(1, stageScrollTop() / max)) : 1;
      const age = START_AGE + (1 - START_AGE) * p;
      // the bus is an event per dispatch — only speak when the value moved
      if (Math.abs(age - last) < 0.003) return;
      last = age;
      publishAge(age);
    };
    write();
    const off = onStageScroll(write);
    window.addEventListener("resize", write, { passive: true });
    return () => {
      off();
      window.removeEventListener("resize", write);
      publishAge(null);
    };
  }, [enabled]);
}
